/** @format */

import { FC, useState, useRef, useEffect, useCallback } from "react";
import { Timer, AlertCircle, Pencil } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";

interface ProcessingTimerProps {
  processingTimeSeconds?: number | null;
  className?: string;
  disabled?: boolean;
  label?: string;
  tooltip?: string;
  status?:
    | "error"
    | "done"
    | "processing"
    | "queued"
    | "pending_verification"
    | "completed";
  errorMessage?: string | null;
  editable?: boolean;
  onProcessingTimeChange?: (seconds: number) => void;
}

const MAX_MINUTES = 999;
const MAX_SECONDS = 59;

function clampMinutes(n: number) {
  return Math.min(MAX_MINUTES, Math.max(0, n));
}
function clampSeconds(n: number) {
  return Math.min(MAX_SECONDS, Math.max(0, n));
}

const ProcessingTimer: FC<ProcessingTimerProps> = ({
  processingTimeSeconds,
  className = "",
  disabled = false,
  label = "Processed in",
  tooltip = "Processed in",
  status,
  errorMessage,
  editable = false,
  onProcessingTimeChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editMinutes, setEditMinutes] = useState(0);
  const [editSeconds, setEditSeconds] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const minutesInputRef = useRef<HTMLInputElement>(null);
  const secondsInputRef = useRef<HTMLInputElement>(null);

  const formatProcessingTime = (seconds: number): string => {
    const total = Math.round(seconds);
    if (total < 60) return `${total}s`;
    if (total < 3600) {
      const m = Math.floor(total / 60);
      const s = total % 60;
      return s ? `${m}m ${s}s` : `${m}m`;
    }
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const openEdit = () => {
    if (!editable || disabled || status === "error") return;
    const total = Math.round(processingTimeSeconds ?? 0);
    setEditMinutes(clampMinutes(Math.floor(total / 60)));
    setEditSeconds(clampSeconds(total % 60));
    setIsEditing(true);
  };

  const closeEdit = useCallback(() => {
    const m =
      minutesInputRef.current != null
        ? clampMinutes(parseInt(minutesInputRef.current.value, 10) || 0)
        : editMinutes;
    const s =
      secondsInputRef.current != null
        ? clampSeconds(parseInt(secondsInputRef.current.value, 10) || 0)
        : editSeconds;
    onProcessingTimeChange?.(m * 60 + s);
    setIsEditing(false);
  }, [editMinutes, editSeconds, onProcessingTimeChange]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (containerRef.current?.contains(next)) return;
      closeEdit();
    },
    [closeEdit],
  );

  useEffect(() => {
    if (!isEditing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsEditing(false);
        return;
      }
      if (e.key === "Enter") {
        closeEdit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEditing, closeEdit]);

  if (!processingTimeSeconds && !disabled && status !== "error" && !editable) {
    return null;
  }

  if (status === "error") {
    const errMsg = errorMessage || "Processing failed";
    const errLabel =
      processingTimeSeconds != null
        ? `Failed after ${formatProcessingTime(processingTimeSeconds)}`
        : "Processing failed";
    return (
      <Tooltip.Provider delayDuration={200}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div
              className={`flex items-center gap-2 text-sm w-fit text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-2 py-1 ${className}`}
            >
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">{errLabel}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="bg-slate-800 text-white border border-slate-600 rounded-md px-2 py-1 text-xs shadow-lg z-50 max-w-xs"
              sideOffset={5}
            >
              {errMsg}
              <Tooltip.Arrow className="fill-slate-800" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  }

  if (isEditing) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center gap-2 text-sm shrink-0 ${className}`}
      >
        <Timer className="w-4 h-4 text-slate-400 shrink-0" />
        <div className="flex items-center gap-1 min-w-0">
          <input
            ref={minutesInputRef}
            type="number"
            min={0}
            max={MAX_MINUTES}
            value={editMinutes}
            onChange={(e) => {
              const v =
                e.target.value === "" ? 0 : parseInt(e.target.value, 10);
              if (!Number.isNaN(v)) setEditMinutes(clampMinutes(v));
            }}
            onBlur={handleBlur}
            aria-label="Minutes"
            className="font-medium bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-slate-200 w-12 min-w-[3rem] text-center focus:outline-none focus:ring-1 focus:ring-slate-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-slate-500 font-medium shrink-0">m</span>
          <input
            ref={secondsInputRef}
            type="number"
            min={0}
            max={MAX_SECONDS}
            value={editSeconds}
            onChange={(e) => {
              const v =
                e.target.value === "" ? 0 : parseInt(e.target.value, 10);
              if (!Number.isNaN(v)) setEditSeconds(clampSeconds(v));
            }}
            onBlur={handleBlur}
            aria-label="Seconds"
            className="font-medium bg-slate-800 border border-slate-600 rounded px-2 py-0.5 text-slate-200 w-12 min-w-[3rem] text-center focus:outline-none focus:ring-1 focus:ring-slate-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-slate-500 font-medium shrink-0">s</span>
        </div>
      </div>
    );
  }

  const display = (
    <div
      role={editable && !disabled ? "button" : undefined}
      tabIndex={editable && !disabled ? 0 : undefined}
      onClick={editable && !disabled ? openEdit : undefined}
      onKeyDown={
        editable && !disabled
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openEdit();
              }
            }
          : undefined
      }
      className={`flex items-center gap-2 text-sm w-fit ${disabled ? "text-slate-500 select-none bg-slate-800 rounded-md px-2 py-1" : "text-slate-400"} ${editable && !disabled ? "cursor-pointer hover:bg-slate-800/80 rounded-md px-2 py-1" : ""} ${className}`}
    >
      <Timer className={`w-4 h-4 ${disabled ? "opacity-50" : ""}`} />
      <span className="font-medium">
        {label}{" "}
        {processingTimeSeconds != null
          ? formatProcessingTime(processingTimeSeconds)
          : "--"}
      </span>
      {editable && !disabled && (
        <Pencil className="w-3.5 h-3.5 opacity-60" aria-hidden />
      )}
    </div>
  );

  return (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{display}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="bg-slate-800 text-white border border-slate-600 rounded-md px-2 py-1 text-xs shadow-lg z-50"
            sideOffset={5}
          >
            {editable && !disabled ? "Click to edit (min and sec)" : tooltip}
            <Tooltip.Arrow className="fill-slate-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default ProcessingTimer;
